import { lazy } from 'react';
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Outlet,
	Route,
} from 'react-router';

import { DataTableMetaProvider } from '@adflow/frontend/ui-data-table';
import { CanRoute } from '@adflow/frontend/util-auth';

import { Root } from './app/_Root';
import App from './app/App';
import GMBTemplateView from './app/GMB/Templates/GMBTemplateView';
import LoginPage from './app/Login/LoginPage';
import { ResetPassword } from './app/Login/ResetPassword';
import { WelcomeView } from './app/Login/WelcomeView';

const FacebookTemplatesList = lazy(
	() => import('./app/Facebook/FacebookTemplates/FacebookTemplatesList')
);
const FacebookTemplateView = lazy(
	() => import('./app/Facebook/FacebookTemplates/FacebookTemplateView')
);
const FacebookPagesList = lazy(() => import('./app/Facebook/Pages/FacebookPagesList'));
const MediasListPage = lazy(() => import('./app/Media/MediasList'));
const NoRoleSelectedPage = lazy(() => import('./app/Roles/NoRoleSelectedPage'));
const RoleFormAddPage = lazy(() => import('./app/Roles/RoleForm/RoleFormAddPage'));
const RoleFormEditPage = lazy(() => import('./app/Roles/RoleForm/RoleFormEditPage'));
const RolesPage = lazy(() => import('./app/Roles/RolesPage'));
const UsersListPage = lazy(() => import('./app/Users/UsersList'));
const UserViewPage = lazy(() => import('./app/Users/UserView'));
const GoogleAdsTemplateView = lazy(
	() => import('./app/GoogleAds/GoogleAdsTemplates/GoogleAdsTemplateView')
);
const GoogleAdsTemplatesList = lazy(
	() => import('./app/GoogleAds/GoogleAdsTemplates/GoogleAdsTemplateList')
);
const KeywordsList = lazy(() => import('./app/GoogleAds/Keywords/KeywordsList'));
const PromotionAssetsList = lazy(
	() => import('./app/GoogleAds/PromotionAssets/PromotionAssetsList')
);
const PromotionAssetView = lazy(() => import('./app/GoogleAds/PromotionAssets/PromotionAssetView'));
const GMBTemplatesList = lazy(() => import('./app/GMB/Templates/GMBTemplateList'));
const PacingQuickView = lazy(() => import('./app/Pacing/PacingQuickView'));

export const router = createBrowserRouter(
	createRoutesFromElements(
		<Route element={<Root />}>
			<Route path="login" element={<LoginPage />} />
			<Route path="reset-password/:token" element={<ResetPassword />} />
			<Route path="welcome/:token" element={<WelcomeView />} />
			<Route path="*" element={<App />}>
				<Route path="" element={<Navigate to="facebook/templates" />} />
				<Route path="facebook">
					<Route path="" element={<Navigate to="media" />} /> {/* TODO: Navigate to templates */}
					<Route path="media" element={<MediasListPage destination="FACEBOOK" />} />
					<Route
						path="templates"
						element={
							<DataTableMetaProvider key="facebook">
								<Outlet />
							</DataTableMetaProvider>
						}
					>
						<Route index element={<FacebookTemplatesList />} />
						<Route path=":templateId" element={<FacebookTemplateView />} />
					</Route>
					<Route path="pages" element={<FacebookPagesList />} />
				</Route>
				<Route path="google-ads">
					<Route path="" element={<Navigate to="templates" />} />
					<Route path="media" element={<MediasListPage destination="GOOGLE" />} />
					<Route
						path="templates"
						element={
							<DataTableMetaProvider key="google">
								<Outlet />
							</DataTableMetaProvider>
						}
					>
						<Route index element={<GoogleAdsTemplatesList />} />
						<Route path=":templateId" element={<GoogleAdsTemplateView />} />
					</Route>
					<Route path="keywords" element={<KeywordsList />} />
					<Route
						path="promotion-assets"
						element={
							<DataTableMetaProvider key="promotion-assets">
								<Outlet />
							</DataTableMetaProvider>
						}
					>
						<Route index element={<PromotionAssetsList />} />
						<Route path="add" element={<PromotionAssetView />} />
						<Route path=":promotionAssetId" element={<PromotionAssetView />} />
					</Route>
				</Route>
				<Route path="google-my-business">
					<Route path="" element={<Navigate to="templates" />} />
					<Route path="media" element={<MediasListPage destination="GMB" />} />
					<Route
						path="templates"
						element={
							<DataTableMetaProvider key="google-my-business">
								<Outlet />
							</DataTableMetaProvider>
						}
					>
						<Route index element={<GMBTemplatesList />} />
						<Route path=":templateId" element={<GMBTemplateView />} />
					</Route>
				</Route>
				<Route
					path="pacing"
					element={<CanRoute I="list" a="Pacing" Component={PacingQuickView} />}
				/>
				<Route path="roles" element={<CanRoute I="list" a="Role" Component={RolesPage} />}>
					<Route path="" element={<NoRoleSelectedPage />} />
					<Route path="add" element={<RoleFormAddPage />} />
					<Route path="*" element={<RoleFormEditPage />} />
				</Route>
				<Route path="users">
					<Route path="" element={<CanRoute I="list" a="User" Component={UsersListPage} />} />
					<Route path=":userId" element={<CanRoute I="view" a="User" Component={UserViewPage} />} />
				</Route>
			</Route>
		</Route>
	)
);
